import { CommitmentUpsellSource } from 'src/api/tracking/types';
import { isPathSessionType } from 'src/apps/Path/continueOnPathQuery';
import { PathSessionType } from 'src/apps/Path/types';
import { ROUTES } from 'src/constants/routes.const';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import { isValidPlansPageSource } from './getPlansPageSource';

type CommitmentNavigation = {
  continueOnPath?: boolean;
  pathwayNodeId?: number | null;
  sessionType?: PathSessionType | null;
  /** Page the learner was on before the lesson. Used when they are not on the path. */
  back?: string | null;
  showReminders?: boolean;
};

/** A same-site path. Anything else is dropped so the return address cannot leave Memrise. */
export const isSafeReturnPath = (value: string | null | undefined): value is string =>
  !!value &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.includes('\\') &&
  !/[\s]/.test(value) &&
  value.length <= 200;

const queryFromNavigation = (
  source: string,
  navigation?: CommitmentNavigation,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (isValidPlansPageSource(source)) params.source = source;

  if (navigation?.continueOnPath) {
    params.continueOnPath = 'true';
    if (typeof navigation.pathwayNodeId === 'number') {
      params.pathwayNodeId = String(navigation.pathwayNodeId);
    }
    if (navigation.sessionType) params.sessionType = navigation.sessionType;
    if (navigation.showReminders) params.showReminders = 'true';
    return params;
  }

  if (isSafeReturnPath(navigation?.back)) params.back = navigation.back;
  return params;
};

/** The commitment upsell ("How committed are you?"), as its own page. */
export const buildCommitmentUpsellUrl = (
  source: CommitmentUpsellSource,
  navigation?: CommitmentNavigation,
) => stringifyUrl(ROUTES.payment.commitment, queryFromNavigation(source, navigation));

/**
 * The condensed plans page opened from Commit. The upsell's source and return query are
 * kept, so the purchase funnel can be joined and the learner still leaves to the right place.
 */
export const buildCommitmentPlansUrl = (source: string): string => {
  const current = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  const navigation: CommitmentNavigation = {};

  if (current.get('continueOnPath') === 'true') {
    navigation.continueOnPath = true;
    const pathwayNodeId = Number(current.get('pathwayNodeId'));
    navigation.pathwayNodeId =
      typeof current.get('pathwayNodeId') === 'string' && Number.isInteger(pathwayNodeId)
        ? pathwayNodeId
        : null;
    const sessionType = current.get('sessionType');
    if (isPathSessionType(sessionType)) navigation.sessionType = sessionType;
    if (current.get('showReminders') === 'true') navigation.showReminders = true;
  } else {
    const back = current.get('back');
    if (isSafeReturnPath(back)) navigation.back = back;
  }

  return stringifyUrl(ROUTES.payment.plans, queryFromNavigation(source, navigation));
};
